import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { doc, setDoc, getDoc, addDoc, collection } from "firebase/firestore";
import { FaUser, FaUserPlus, FaEye, FaEyeSlash } from "react-icons/fa";
import "../App.css";
import { auth, db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { isJackOnboardingComplete, clearJackOnboardingFlag } from "../utils/jackSettings";

const LOGO_SRC = `${process.env.PUBLIC_URL || ''}/logo_shokiq_horizontal.svg`;

/**
 * Maps Firebase Auth error codes to user-friendly messages.
 * @param {Error} err - Error from Firebase Auth
 * @param {boolean} isSignUp - Whether the error came from sign-up (affects some messages)
 * @returns {string} User-friendly error message
 */
const getAuthErrorMessage = (err, isSignUp) => {
  const code = err?.code || "";
  const messages = {
    "auth/invalid-email": "Please enter a valid email address.",
    "auth/user-disabled": "This account has been disabled. Please contact support.",
    "auth/user-not-found": "No account found with this email. Check the address or create an account.",
    "auth/wrong-password": "Incorrect password. Please try again.",
    "auth/invalid-credential": "Invalid email or password. Please check and try again.",
    "auth/email-already-in-use": "This email is already registered. Try logging in or use a different email.",
    "auth/weak-password": "Password is too weak. Use at least 6 characters.",
    "auth/too-many-requests": "Too many attempts. Please wait a moment and try again.",
    "auth/network-request-failed": "Connection problem. Please check your internet and try again.",
    "auth/operation-not-allowed": "Sign-in is not available. Please contact support.",
    "auth/requires-recent-login": "For security, please log in again to continue.",
    "permission-denied":
      "Missing Firestore permission. Deploy rules from shokiq-pwa/firestore.rules (users, runs, mail) in Firebase Console → Firestore → Rules, then Publish.",
  };
  return messages[code] || (err?.message || "Something went wrong. Please try again.");
};

const Login = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("signup") === "true") {
      setIsSignUp(true);
    }
  }, [location]);

  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const docRef = doc(db, "users", user.uid);
        // Right after sign-up, Auth updates before Firestore users/{uid} exists — retry instead of signing out.
        let docSnap = await getDoc(docRef);
        let attempt = 0;
        while (!cancelled && !docSnap.exists() && attempt < 12) {
          await new Promise((r) => setTimeout(r, Math.min(400, 80 * (attempt + 1))));
          if (cancelled) return;
          docSnap = await getDoc(docRef);
          attempt += 1;
        }
        if (cancelled) return;
        if (!docSnap.exists()) {
          setError("Your account could not be found. Please contact support.");
          await signOut(auth);
          return;
        }
        if (!docSnap.data().accepted) {
          setError("Your account is pending approval. We'll notify you when it's ready.");
          await signOut(auth);
          return;
        }
        if (!isJackOnboardingComplete()) {
          navigate("/bike-onboarding", { replace: true });
          return;
        }
        navigate("/home", { replace: true });
      } catch (e) {
        if (cancelled) return;
        const code = e?.code || "";
        setError(
          code === "permission-denied"
            ? getAuthErrorMessage(e, false)
            : e?.message || "Could not verify your account. Please try again.",
        );
        try {
          await signOut(auth);
        } catch (_s) {
          /* ignore */
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, navigate]);

  const handleSignUp = async () => {
    setError("");
    if (!email || !password) {
      setError("Please enter your email and password.");
      return;
    }
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const newUser = userCredential.user;

      // Clear before setDoc so the auth redirect effect cannot send this user to /home while a stale flag is still set.
      clearJackOnboardingFlag();

      await setDoc(doc(db, "users", newUser.uid), {
        email,
        username,
        accepted: true,
        credits: 100,
      });

      try {
        await addDoc(collection(db, "mail"), {
          to: "mail@jeromelacote.com",
          message: {
            subject: "New user signup!",
            text: `A new user signed up with email: ${email}`,
          },
        });
      } catch (mailErr) {
        console.warn("Signup: mail queue write skipped (optional):", mailErr?.code || mailErr?.message);
      }

      setEmail("");
      setPassword("");
      setIsSignUp(false);
      navigate("/bike-onboarding", { replace: true });
    } catch (err) {
      setError(getAuthErrorMessage(err, true));
    }
  };

  const handleLogin = async () => {
    setError("");
    if (!email || !password) {
      setError("Please enter your email and password.");
      return;
    }
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      setError(getAuthErrorMessage(err, false));
    }
  };

  if (user) {
    return (
      <div className="login-page">
        <img src={LOGO_SRC} alt="ShokIQ" className="login-logo" />
        <p className="login-description" style={{ color: "#c5c3ce", textAlign: "center" }}>
          Signing you in…
        </p>
      </div>
    );
  }

  return (
    <div className="login-page">
      <img src={LOGO_SRC} alt="ShokIQ" className="login-logo" />
      <div className="login-container">
        <div className="login-header">
          {isSignUp ? <FaUserPlus className="login-icon" /> : <FaUser className="login-icon" />}
          <h2 className="login-title">{isSignUp ? "Create Your Account" : "Login to Your Account"}</h2>
        </div>

      {error && <p className="error-text">{error}</p>}

      {isSignUp && (
        <input
          type="text"
          placeholder="Username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          className="login-input"
        />
      )}

      <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="login-input" />
      <div className="password-input-wrapper">
        <input
          type={showPassword ? "text" : "password"}
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="login-input password-input"
        />
        <button
          type="button"
          className="password-toggle-btn"
          onClick={() => setShowPassword((prev) => !prev)}
          aria-label={showPassword ? "Hide password" : "Show password"}
          title={showPassword ? "Hide password" : "Show password"}
        >
          {showPassword ? <FaEyeSlash /> : <FaEye />}
        </button>
      </div>

      <div className="login-actions">
        <button
          type="button"
          className="login-cancel-btn"
          onClick={() => navigate('/')}
        >
          Cancel
        </button>
        {isSignUp ? (
          <button type="button" onClick={handleSignUp} className="primary-btn">
            Sign Up
          </button>
        ) : (
          <button type="button" onClick={handleLogin} className="primary-btn">
            Login
          </button>
        )}
      </div>

      <p className="login-switch">
        {isSignUp ? "Already have an account?" : "No account yet?"}{" "}
        <span className="login-switch-link" onClick={() => setIsSignUp(!isSignUp)}>
          {isSignUp ? "Login" : "Create one"}
        </span>
      </p>
      </div>
    </div>
  );
};

export default Login;
